<template>
  <SettingsPageLayout>
    <DetailForm
      v-if="loaded"
      title="Event Scheduling"
      description="Manage how Candidates and Teammates schedule various events related to the recruiting process with your team/brokerage."
      action="Save"
      hidecancel="true"
      @handle-action="updateTeam"
    >
      <ToggleInput
        label="Allow Qualified Candidates to Schedule Immediately"
        description="If a qualified candidate applies, redirect them to your calendar so they can schedule their phone screen immediately."
        :model-value="team.allowQualifiedCandidatesToScheduleImmediately"
        @update:model-value="team.allowQualifiedCandidatesToScheduleImmediately = $event"
      />
      <RadioSelect
        v-model="team.calendarOption"
        label="Scheduling Platform"
        :options="[
          {
            label: 'Humaniz',
            description:
              'When Humaniz is selected as the primary form of scheduling all scheduling happens through our platform. Users must be assigned to the respective events for scheduling to work for that event type.',
          },
          {
            label: 'Calendly',
            description:
              'When Calendly is selected as the primary form of scheduling all scheduling happens through their platform. Calendly api token and link will be required to go live with syndication.',
          },
        ]"
      />
      <template v-if="team.calendarOption == 'Humaniz'">
        <Card v-if="!viewPhoneScreenOptions">
          <div class="flex justify-between">
            <div class="flex justify-between cursor-pointer" @click="viewPhoneScreenOptions = !viewPhoneScreenOptions">
              <div class="text-md font-medium leading-6 text-gray-900 mr-2">Phone Screen Options</div>
              <vue-feather type="more-horizontal" class="mt-0.5 h-5" />
            </div>
            <router-link :to="`/interview/${team.slug}?event_type=phone_screen`">
              <TButton label="Preview" color="indigo"> </TButton>
            </router-link>
          </div>
        </Card>
        <Card v-else class="overflow-visible">
          <div
            class="text-md font-medium leading-6 text-gray-900 mr-2 cursor-pointer"
            @click="viewPhoneScreenOptions = !viewPhoneScreenOptions"
          >
            Phone Screen Options
          </div>
          <SelectMenu
            class="mt-4"
            label="Phone Screen Event Duration"
            :initial-value="team.phoneScreenEventDuration ? team.phoneScreenEventDuration : '30'"
            :options="['15 minutes', '30 minutes', '45 minutes', '60 minutes']"
            @selected-option="team.phoneScreenEventDuration = $event"
          />
          <TextInput
            class="mt-6"
            label="Phone Screen Event Name"
            :model-value="team.phoneScreenEventName"
            @update:model-value="value => (team.phoneScreenEventName = value)"
          />
          <TextAreaInput
            class="mt-4"
            label="Phone Screen Event Description"
            :model-value="team.phoneScreenEventDescription"
            @update:model-value="team.phoneScreenEventDescription = $event"
          />
          <SelectMenu
            class="mt-4"
            label="Phone Screen Event Location"
            :initial-value="team.phoneScreenEventLocation ? team.phoneScreenEventLocation : 'phone call'"
            :options="['phone call', 'humaniz conferencing', 'standard conferencing', 'physical location']"
            @selected-option="team.phoneScreenEventLocation = $event"
          />
          <AddressInput
            v-if="team.phoneScreenEventLocation == 'physical location'"
            class="mt-4"
            label="Phone Screen Event Address"
            :model-value="team.phoneScreenEventAddress"
            @update:model-value="team.phoneScreenEventAddress = $event"
          />
        </Card>
        <Card v-if="!viewInterviewOptions">
          <div class="flex justify-between">
            <div class="flex justify-between cursor-pointer" @click="viewInterviewOptions = !viewInterviewOptions">
              <div class="text-md font-medium leading-6 text-gray-900 mr-2">Interview Options</div>
              <vue-feather type="more-horizontal" class="mt-0.5 h-5" />
            </div>
            <router-link :to="`/interview/${team.slug}?event_type=interview`">
              <TButton label="Preview" color="indigo"> </TButton>
            </router-link>
          </div>
        </Card>
        <Card v-else class="overflow-visible">
          <div
            class="text-md font-medium leading-6 text-gray-900 mr-2 cursor-pointer"
            @click="viewInterviewOptions = !viewInterviewOptions"
          >
            Interview Options
          </div>
          <SelectMenu
            class="mt-4"
            label="Interview Event Duration"
            :initial-value="team.interviewEventDuration ? team.interviewEventDuration : '30'"
            :options="['15 minutes', '30 minutes', '45 minutes', '60 minutes']"
            @selected-option="team.interviewEventDuration = $event"
          />
          <TextInput
            class="mt-6"
            label="Interview Event Name"
            :model-value="team.interviewEventName"
            @update:model-value="value => (team.interviewEventName = value)"
          />
          <TextAreaInput
            class="mt-4"
            label="Interview Event Description"
            :model-value="team.interviewEventDescription"
            @update:model-value="team.interviewEventDescription = $event"
          />
          <SelectMenu
            class="mt-4"
            label="Interview Event Location"
            :initial-value="team.interviewEventLocation ? team.interviewEventLocation : 'phone call'"
            :options="['phone call', 'humaniz conferencing', 'standard conferencing', 'physical location']"
            @selected-option="team.interviewEventLocation = $event"
          />
          <AddressInput
            v-if="team.interviewEventLocation == 'physical location'"
            class="mt-4"
            label="Interview Event Address"
            :model-value="team.interviewEventAddress"
            @update:model-value="team.interviewEventAddress = $event"
          />
        </Card>
        <Card v-if="!viewPipelineReviewOptions">
          <div class="flex justify-between">
            <div
              class="flex justify-between cursor-pointer"
              @click="viewPipelineReviewOptions = !viewPipelineReviewOptions"
            >
              <div class="text-md font-medium leading-6 text-gray-900 mr-2">Pipeline Review Options</div>
              <vue-feather type="more-horizontal" class="mt-0.5 h-5" />
            </div>
            <router-link :to="`/interview/${team.slug}?event_type=pipeline_review`">
              <TButton label="Preview" color="indigo"> </TButton>
            </router-link>
          </div>
        </Card>
        <Card v-else class="overflow-visible">
          <div
            class="text-md font-medium leading-6 text-gray-900 mr-2 cursor-pointer"
            @click="viewPipelineReviewOptions = !viewPipelineReviewOptions"
          >
            Pipeline Review Options
          </div>
          <SelectMenu
            class="mt-4"
            label="Pipeline Review Event Duration"
            :initial-value="team.pipelineReviewEventDuration ? team.pipelineReviewEventDuration : '30'"
            :options="['15 minutes', '30 minutes', '45 minutes', '60 minutes']"
            @selected-option="team.pipelineReviewEventDuration = $event"
          />
          <TextInput
            class="mt-6"
            label="Pipeline Review Event Name"
            :model-value="team.pipelineReviewEventName"
            @update:model-value="value => (team.pipelineReviewEventName = value)"
          />
          <TextAreaInput
            class="mt-4"
            label="Pipeline Review Event Description"
            :model-value="team.pipelineReviewEventDescription"
            @update:model-value="team.pipelineReviewEventDescription = $event"
          />
          <SelectMenu
            class="mt-4"
            label="Pipeline Review Event Location"
            :initial-value="team.pipelineReviewEventLocation ? team.pipelineReviewEventLocation : 'phone call'"
            :options="['phone call', 'humaniz conferencing', 'standard conferencing', 'physical location']"
            @selected-option="team.pipelineReviewEventLocation = $event"
          />
          <AddressInput
            v-if="team.pipelineReviewEventLocation == 'physical location'"
            class="mt-4"
            label="Pipeline Review Event Address"
            :model-value="team.pipelineReviewEventAddress"
            @update:model-value="team.pipelineReviewEventAddress = $event"
          />
        </Card>
      </template>
      <template v-else>
        <TextInput
          label="Calendly Link"
          placeholder="https://calendly.com/ehomesg/15-min-phone-interview"
          :model-value="team.calendlyLink"
          @update:model-value="value => (team.calendlyLink = value)"
        />
        <TextAreaInput
          class="mt-4"
          label="Calendly Api Token"
          placeholder="eHomes"
          :model-value="team.calendlyApiToken"
          @update:model-value="value => (team.calendlyApiToken = value)"
        />
        <TextInput
          class="mt-4"
          label="Followup Calendly Link"
          placeholder="https://calendly.com/ehomesg/15-min-phone-interview"
          :model-value="team.followupCalendlyLink"
          @update:model-value="team.followupCalendlyLink = $event"
        />
      </template>
    </DetailForm>
  </SettingsPageLayout>
</template>

<script>
import SettingsPageLayout from '../../SettingsPageLayout/index.vue';
import RailsUtility from '../../../utilities/rails';
import DetailForm from '../../shared/DetailForm.vue';
import RadioSelect from '../../shared/RadioSelect.vue';
import AddressInput from '../../shared/AddressInput.vue';
import Card from '../../Card/index.vue';

export default {
  components: {
    SettingsPageLayout,
    DetailForm,
    RadioSelect,
    Card,
    AddressInput,
  },
  data() {
    return {
      team: {
        id: '',
        allowQualifiedCandidatesToScheduleImmediately: false,
        calendarOption: '',
        phoneScreenEventName: '',
        phoneScreenEventDescription: '',
        phoneScreenEventDuration: '',
        phoneScreenEventLocation: '',
        phoneScreenEventAddress: '',
        interviewEventName: '',
        interviewEventDescription: '',
        interviewEventDuration: '',
        interviewEventLocation: '',
        interviewEventAddress: '',
        pipelineReviewEventDuration: '',
        pipelineReviewEventName: '',
        pipelineReviewEventDescription: '',
        pipelineReviewEventLocation: '',
        pipelineReviewEventAddress: '',
        calendlyLink: '',
        calendlyApiToken: '',
        followupCalendlyLink: '',
        calendlyApiActive: false,
        calendlyApiError: '',
      },
      viewPhoneScreenOptions: false,
      viewInterviewOptions: false,
      viewPipelineReviewOptions: false,
      loaded: false,
    };
  },
  mounted() {
    this.getTeam();
  },
  methods: {
    getTeam() {
      const self = this;
      self.$store.commit('general/showLoadingSpinner');
      fetch(`/api/teams/1`)
        .then(response => {
          self.$store.commit('general/hideLoadingSpinner');
          return response.json();
        })
        .then(data => {
          self.team.id = data.id;
          self.team.allowQualifiedCandidatesToScheduleImmediately =
            data.record.allow_qualified_candidates_to_schedule_immediately;
          self.team.calendarOption = data.record.calendar_option;
          self.team.phoneScreenEventName = data.record.phone_screen_event_name;
          self.team.phoneScreenEventDescription = data.record.phone_screen_event_description;
          self.team.phoneScreenEventDuration = RailsUtility.convertFromEnumFormat(
            data.record.phone_screen_event_duration
          );
          self.team.phoneScreenEventLocation = RailsUtility.convertFromEnumFormat(
            data.record.phone_screen_event_location
          );
          self.team.phoneScreenEventAddress = data.record.phone_screen_event_address;
          self.team.interviewEventName = data.record.interview_event_name;
          self.team.interviewEventDescription = data.record.interview_event_description;
          self.team.interviewEventDuration = RailsUtility.convertFromEnumFormat(data.record.interview_event_duration);
          self.team.interviewEventLocation = RailsUtility.convertFromEnumFormat(data.record.interview_event_location);
          self.team.interviewEventAddress = data.record.interview_event_address;
          self.team.pipelineReviewEventName = data.record.pipeline_review_event_name;
          self.team.pipelineReviewEventDescription = data.record.pipeline_review_event_description;
          self.team.pipelineReviewEventDuration = RailsUtility.convertFromEnumFormat(
            data.record.pipeline_review_event_duration
          );
          self.team.pipelineReviewEventLocation = RailsUtility.convertFromEnumFormat(
            data.record.pipeline_review_event_location
          );
          self.team.pipelineReviewEventAddress = data.record.pipeline_review_event_address;
          self.team.calendlyLink = data.record.calendly_link;
          self.team.calendlyApiToken = data.record.calendly_api_token;
          self.team.followupCalendlyLink = data.record.calendly_link_followup;
          self.team.calendlyApiActive = data.record.calendly_api_active;
          self.team.calendlyApiError = data.record.calendly_api_error;
          self.team.slug = data.record.slug;
          self.loaded = true;
        });
    },
    updateTeam() {
      const self = this;
      self.$store.commit('general/showLocalLoadingSpinner');
      fetch(`/api/teams/${self.team.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          allow_qualified_candidates_to_schedule_immediately: self.team.allowQualifiedCandidatesToScheduleImmediately,
          calendar_option: self.team.calendarOption,
          phone_screen_event_name: self.team.phoneScreenEventName,
          phone_screen_event_description: self.team.phoneScreenEventDescription,
          phone_screen_event_duration: RailsUtility.convertToEnumFormat(self.team.phoneScreenEventDuration),
          phone_screen_event_location: RailsUtility.convertToEnumFormat(self.team.phoneScreenEventLocation),
          phone_screen_event_address: self.team.phoneScreenEventAddress,
          interview_event_name: self.team.interviewEventName,
          interview_event_description: self.team.interviewEventDescription,
          interview_event_duration: RailsUtility.convertToEnumFormat(self.team.interviewEventDuration),
          interview_event_location: RailsUtility.convertToEnumFormat(self.team.interviewEventLocation),
          interview_event_address: self.team.interviewEventAddress,
          pipeline_review_event_name: self.team.pipelineReviewEventName,
          pipeline_review_event_description: self.team.pipelineReviewEventDescription,
          pipeline_review_event_duration: RailsUtility.convertToEnumFormat(self.team.pipelineReviewEventDuration),
          pipeline_review_event_location: RailsUtility.convertToEnumFormat(self.team.pipelineReviewEventLocation),
          pipeline_review_event_address: self.team.pipelineReviewEventAddress,
          calendly_link: self.team.calendlyLink,
          calendly_api_token: self.team.calendlyApiToken,
          calendly_link_followup: self.team.followupCalendlyLink,
        }),
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': document.querySelectorAll('[name="csrf-token"]')[0].content,
        },
      })
        .then(response => {
          return response.json();
        })
        .then(data => {
          self.$store.commit('general/hideLocalLoadingSpinner');
          if (data.status != 'error') {
            self.$store.commit('general/showAlert', {
              alertType: 'success',
              alertTitle: 'Scheduling settings updated',
              alertDescription:
                'Your Scheduling settings were successfully updated.  Use the button below to dismiss this alert.',
            });
          } else {
            self.$store.commit('general/showAlert', {
              alertType: 'error',
              alertTitle: 'Scheduling settings update error',
              alertDescription: data.message,
            });
          }
        });
    },
  },
};
</script>
